/**
 * InlineReviewSection — Single-card review queue with inline cycling
 *
 * Shows one uncertain transaction (or match suggestion) at a time.
 * When the user categorizes one, the next slides in automatically.
 * Dashed-border cream card signals "incomplete / needs attention"
 * without competing with the solid Activity and Accounts cards.
 *
 * Auto-hides when all items are resolved.
 */

import { T } from '@/styles/tokens';
import { InlineReviewCard } from '@/components/money/InlineReviewCard';
import { MatchSuggestionCard } from '@/components/money/MatchSuggestionCard';
import type { ArtTransaction } from '@/hooks/useArtTransactions';
import type { MatchSuggestion } from '@/hooks/useMatchSuggestions';

interface InlineReviewSectionProps {
  uncertainTransactions: ArtTransaction[];
  matchSuggestions: MatchSuggestion[];
  onCategorize: (txn: ArtTransaction, category: string) => void;
  onMatchLink: (suggestion: MatchSuggestion) => void;
  onMatchDismiss: (suggestion: MatchSuggestion) => void;
}

export function InlineReviewSection({
  uncertainTransactions,
  matchSuggestions,
  onCategorize,
  onMatchLink,
  onMatchDismiss,
}: InlineReviewSectionProps) {
  const totalItems = uncertainTransactions.length + matchSuggestions.length;
  if (totalItems === 0) return null;

  // Show match suggestions first (higher priority), then uncertain transactions
  const hasMatch = matchSuggestions.length > 0;
  const currentMatch = hasMatch ? matchSuggestions[0] : null;
  const currentUncertain = !hasMatch && uncertainTransactions.length > 0 ? uncertainTransactions[0] : null;

  return (
    <div style={{ marginTop: 24 }}>
      {/* Section label with counter */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
      }}>
        <p style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '1.2px',
          color: T.textMuted,
          textTransform: 'uppercase' as const,
          margin: 0,
          fontFamily: T.fontFamily,
        }}>
          To review
        </p>
        {totalItems > 1 && (
          <span style={{
            fontSize: 10,
            fontWeight: 600,
            color: T.textMuted,
            fontFamily: T.fontFamily,
          }}>
            {totalItems} remaining
          </span>
        )}
      </div>

      {/* Single card — cycling handled by React key swap when list changes */}
      {currentMatch ? (
        <MatchSuggestionCard
          key={currentMatch.id}
          suggestion={currentMatch}
          onLink={() => onMatchLink(currentMatch)}
          onDismiss={() => onMatchDismiss(currentMatch)}
        />
      ) : currentUncertain ? (
        <InlineReviewCard
          key={currentUncertain.id}
          txn={currentUncertain}
          onCategorize={onCategorize}
        />
      ) : null}

      {/* Progress dots — only when multiple items */}
      {totalItems > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 5,
          paddingTop: 8,
        }}>
          {Array.from({ length: totalItems }, (_, i) => (
            <div
              key={i}
              style={{
                width: i === 0 ? 16 : 6,
                height: 6,
                borderRadius: 3,
                background: i === 0 ? T.warning : 'rgba(224,122,61,0.18)',
                transition: 'all 280ms ease',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
